import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import { MdNumbers, MdOutlineFreeCancellation } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const SponsorshipMembership = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const fullNamePath = "additionalInfo.sponsor.fullName";
  const fullNameLabel =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP.FULLNAME;
  const fullNameField = bindField(fullNamePath, {
    label: fullNameLabel,
    required: isFieldRequired(fullNamePath),
    onBlur: () => setPercent(initialValues),
  });

  const slmIdPath = "additionalInfo.sponsor.slmId";
  const slmIdLabel =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP.SLM_ID;
  const slmIdField = bindField(slmIdPath, {
    label: slmIdLabel,
    required: isFieldRequired(slmIdPath),
    onBlur: () => setPercent(initialValues),
  });

  const monthsOfferedPath = "additionalInfo.sponsor.monthsOffered";
  const monthsOfferedLabel =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP
      .MONTHS_OFFERED.LABEL;
  const monthsOfferedField = bindField(monthsOfferedPath, {
    label: monthsOfferedLabel,
    required: isFieldRequired(monthsOfferedPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const isSpouseAMemberPath = "additionalInfo.sponsor.isSpouseAMember";
  const isSpouseAMemberLabel =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP.COUPLE_OFFER
      .IS_SPOUSE_A_MEMBER.LABEL;
  const isSpouseAMemberField = bindField(isSpouseAMemberPath, {
    label: isSpouseAMemberLabel,
    onChange: (value) => {
      if (value === false) {
        set(initialValues, "additionalInfo.sponsor.spouseIdentifier", undefined);
      }
      setDisabled((prev) => ({
        ...prev,
        additionalInfo: {
          ...prev.additionalInfo,
          sponsor: {
            ...prev.additionalInfo.sponsor,
            spouseIdentifier: !value,
          },
        },
      }));
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const spouseIdentifierPath = "additionalInfo.sponsor.spouseIdentifier";
  const spouseIdentifierLabel =
    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP.COUPLE_OFFER
      .ADHERENT_NUMBER.LABEL;
  const spouseIdentifierField = bindField(spouseIdentifierPath, {
    label: spouseIdentifierLabel,
    required: true,
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADVANTAGES}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADVANTAGES.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP
                  .TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={fullNameField.path}
                    label={fullNameField.label}
                    required={fullNameField.required}
                    disabled={fullNameField.disabled}
                  >
                    {(field) => (
                      <Input
                        optional
                        placeholder={fullNameLabel}
                        label={fullNameLabel}
                        icon={<MdOutlineFreeCancellation size={16} />}
                        type="custom"
                        normalizeText={true}
                        defaultValue={fullNameField.defaultValue}
                        {...field}
                        onChange={fullNameField.onChange}
                        onBlur={fullNameField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={slmIdField.path}
                    label={slmIdField.label}
                    required={slmIdField.required}
                    disabled={slmIdField.disabled}
                  >
                    {(field) => (
                      <Input
                        optional
                        placeholder={slmIdLabel}
                        label={slmIdLabel}
                        icon={<MdOutlineFreeCancellation size={16} />}
                        type="custom"
                        trim={true}
                        defaultValue={slmIdField.defaultValue}
                        {...field}
                        onChange={slmIdField.onChange}
                        onBlur={slmIdField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                <Col lg={24} xs={24}>
                  <FormField
                    path={monthsOfferedField.path}
                    label={monthsOfferedField.label}
                    required={monthsOfferedField.required}
                    disabled={monthsOfferedField.disabled}
                  >
                    {(field) => (
                      <Select
                        theme="light"
                        optional
                        options={[1, 2, 3, 4, 5, 6, 7, 8].map((value) => ({
                          value,
                          label: value,
                        }))}
                        placeholder={
                          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION
                            .SPONSORSHIP_MEMBERSHIP.MONTHS_OFFERED.PLACEHOLDER
                        }
                        label={monthsOfferedLabel}
                        icon={<MdNumbers size={16} />}
                        defaultValue={monthsOfferedField.defaultValue}
                        value={get(initialValues, monthsOfferedPath)}
                        {...field}
                        onChange={monthsOfferedField.onChange}
                        onBlur={monthsOfferedField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form
              type="highlight"
              title={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.SPONSORSHIP_MEMBERSHIP
                  .COUPLE_OFFER.TITLE
              }
            >
              <Row gutter={[16, 16]}>
                <Col lg={24} xs={24}>
                  <FormField
                    path={isSpouseAMemberField.path}
                    label={isSpouseAMemberField.label}
                    required={isSpouseAMemberField.required}
                    disabled={isSpouseAMemberField.disabled}
                  >
                    {(field) => (
                      <Input
                        type="switch"
                        label={isSpouseAMemberLabel}
                        checked={
                          get(initialValues, isSpouseAMemberPath) === true
                        }
                        {...field}
                        onChange={isSpouseAMemberField.onChange}
                        onBlur={isSpouseAMemberField.onBlur}
                      />
                    )}
                  </FormField>
                </Col>
                {get(initialValues, isSpouseAMemberPath) && (
                  <Col lg={24} xs={24}>
                    <FormField
                      path={spouseIdentifierField.path}
                      label={spouseIdentifierField.label}
                      required={spouseIdentifierField.required}
                      disabled={spouseIdentifierField.disabled}
                    >
                      {(field) => (
                        <Input
                          placeholder={
                            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION
                              .SPONSORSHIP_MEMBERSHIP.COUPLE_OFFER
                              .ADHERENT_NUMBER.PLACEHOLDER
                          }
                          label={spouseIdentifierLabel}
                          icon={<MdOutlineFreeCancellation size={16} />}
                          type="custom"
                          trim={true}
                          defaultValue={spouseIdentifierField.defaultValue}
                          {...field}
                          onChange={spouseIdentifierField.onChange}
                          onBlur={spouseIdentifierField.onBlur}
                        />
                      )}
                    </FormField>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default SponsorshipMembership;
